import { API_BASE_URL, ApiError } from './client';

export interface CalendarSubscriptionStatus {
  active: boolean;
  issuedAt: string | null;
}

/**
 * The reply that creates a subscription, and the only place the URL exists.
 *
 * There is deliberately no endpoint that reads it back. A feed URL is a bearer credential with no
 * second factor and no expiry, so a page that could re-display it would hand it to whoever opens
 * the settings screen next on a shared family laptop. Losing it means issuing a new one, which
 * revokes the old — the same act as replacing a lost phone, which is the only reason anyone
 * presses it.
 */
export interface IssuedCalendarSubscription {
  url: string;
  issuedAt: string;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }
    throw new ApiError(
      response.status,
      problem.detail ?? problem.title ?? `Failed (${response.status}).`,
    );
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const calendarApi = {
  status: () => call<CalendarSubscriptionStatus>('/calendar/subscription/'),

  /** Issues a feed, revoking any the person already holds. The URL comes back once. */
  issue: () =>
    call<IssuedCalendarSubscription>('/calendar/subscription/', { method: 'POST' }),

  revoke: () => call<void>('/calendar/subscription/', { method: 'DELETE' }),
};
